import { dssDebtJoin, dssDebtToken } from '@1delta/data-sdk'
import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { DssMarketsRaw } from './types'

/**
 * Synthesized per-ilk lender key, e.g. `SKY_1_ETH_A` / `USDD_1_WBTC_A`. The
 * CHAIN ID is part of the key (Fluid/River convention) because two dss
 * deployments — even of the same brand — are INDEPENDENT Maker stacks that
 * could file the same ilk string.
 *
 * **`_` IS THE ONLY SEPARATOR — the ilk's own `-` is re-spelled to `_`.**
 * Maker ilks are the first market suffixes in the codebase that contain a
 * hyphen (`ETH-A`, `PSM-USDT-A`), and a key mixing both separators cannot
 * survive a round-trip through any case- or slug-mapping layer: a consumer
 * that lower-cases on `_` and restores on `-` cannot tell which dashes were
 * structure and which were payload. That is not hypothetical — it silently
 * resolved `sky-1-wbtc-a` to the wrong lender in the allocator UI. Keys are
 * therefore hyphen-free, and the real ilk is recovered by `dssKeyParts`.
 *
 * Safe because a Maker ilk never contains `_` (the on-chain convention is
 * `<GEM>-<CLASS>`), making `-` ⇄ `_` injective over the roster; the metadata
 * generators reject an ilk carrying `_` so that stays true.
 */
export function dssLenderKey(
  lender: string,
  chainId: string | number,
  ilk: string,
): string {
  return `${lender}_${chainId}_${ilkToKeySegment(ilk)}`
}

/** Ilk → key segment: `WBTC-A` → `WBTC_A`. */
export const ilkToKeySegment = (ilk: string): string => ilk.replace(/-/g, '_')

/** Key segment → ilk: `WBTC_A` → `WBTC-A`. Inverse of the above. */
export const keySegmentToIlk = (seg: string): string => seg.replace(/_/g, '-')

/** Brands whose per-ilk keys this parser recognises. */
const DSS_KEY_PREFIXES = ['USDD', 'SKY'] as const
// The suffix accepts `-` as well as `_` purely for TOLERANCE on input (see
// below); everything we emit uses `_`.
const DSS_KEY_RE = new RegExp(
  `^(${DSS_KEY_PREFIXES.join('|')})_(\\d+)_([A-Z0-9][A-Z0-9_-]*)$`,
)

/**
 * Recover `{ lender, chainId, ilk }` from a per-market key (or undefined),
 * with the ilk in its true on-chain spelling (`WBTC_A` → `WBTC-A`).
 *
 * **Tolerant on input, canonical on output.** The canonical key is
 * hyphen-free (see `dssLenderKey`), but this also accepts the legacy
 * hyphenated form `SKY_1_WBTC-A` and any mixture, because those keys were
 * already emitted into caller databases and bookmarks. Both spellings map to
 * the same ilk, so a stale link keeps resolving instead of falling through to
 * "unknown lender". The leading `\d+_` disambiguates from the bare
 * `SKY` / `USDD` key.
 */
export function dssKeyParts(
  key: string,
): { lender: string; chainId: string; ilk: string } | undefined {
  const m = key.match(DSS_KEY_RE)
  if (!m) return undefined
  return { lender: m[1], chainId: m[2], ilk: keySegmentToIlk(m[3]) }
}

const WAD = 1e18
const RAY = 1e27
const RAD = 1e45
const YEAR_SECONDS = 31_536_000

function toHuman(raw: bigint, decimals: number): number {
  return Number(raw) / 10 ** decimals
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map one dss deployment's on-chain batch into the shared
 * `MorphoGeneralPublicResponse` shape, keyed `<BRAND>_<chainId>_<ILK>` — one
 * key per collateral ilk. Brand-agnostic: `SKY` (the original MakerDAO) and
 * `USDD` (its fork) differ only in the metadata rows fed in here.
 *
 * Per market:
 *  - COLLATERAL entry: totals = the gem-join balance (the Vat keeps no
 *    per-ilk ink total; the adapter custodies locked + unswept gems);
 *    LTV = 1/mat; liquidation penalty = chop − 1 (Dog.chop, wad).
 *  - LOAN entry (DAI / USDD): `totalDebt` = Art × rate (rad → human);
 *    `variableBorrowRate` = the stability fee as a nominal APR percent —
 *    `(duty − RAY)/RAY × YEAR_SECONDS × 100`, the same annualisation as the
 *    Pot's dsr in the savings fetcher (never `^ seconds − 1`, which is the
 *    APY); `borrowLiquidity` = ceiling headroom `(line − Art × rate)/1e45`.
 *    There is NO protocol supply side (the stable is Vat-minted) — the earn
 *    side is the savings token (sDAI / sUSDD), carried by the savings
 *    provider, so `totalDeposits` on the loan row is 0 and `depositRate` 0.
 *  - Collateral price: Vat.spot × mat (both ray) recovers the par-adjusted
 *    OSM price without reading the pip (whitelisted `peek` would revert);
 *    shared price map as fallback.
 */
export function convertDssMarketsToResponse(
  raw: DssMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  const chainData = raw?.chainData
  if (!cfg || !chainData) return out

  // Tolerant field readers — USDD's published config spells the debt leg
  // `usdd`/`usddJoin`, Sky's spells it `debtToken`/`debtJoin`.
  const rawDebtAddr = dssDebtToken(cfg)
  if (!rawDebtAddr) return out
  const debtAddr = rawDebtAddr.toLowerCase()
  const debtJoin = dssDebtJoin(cfg)
  const debtDecimals = cfg.debtDecimals ?? 18
  const debtToken = tokens[debtAddr]
  const debtSymbol = debtToken?.symbol ?? cfg.debtSymbol ?? 'USDD'
  const debtPriceKey =
    toOracleKey(debtToken?.assetGroup!) || toGenericPriceKey(debtAddr, chainId)
  // Redeemable at par via the PSMs — peg to 1 USD unless the shared price
  // map knows better.
  const debtPrice = prices[debtPriceKey] || 1

  for (const m of raw.markets ?? []) {
    const market = m.market
    const lenderKey = dssLenderKey(raw.lender, chainId, market.ilk)
    const collAddr = market.collToken.toLowerCase()
    const collDecimals = market.collDecimals
    const collToken = tokens[collAddr]
    const collSymbol =
      collToken?.symbol ?? market.collSymbol ?? market.ilk.split('-')[0]
    const marketName = market.name ?? `${debtSymbol} / ${collSymbol}`

    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    // Vat.spot (ray) = price / (par × mat); × mat (ray) recovers the price
    // (par is 1e27 on every known Maker deployment).
    const spotDerivedPrice =
      m.spot !== null && m.mat !== null
        ? (Number(m.spot) / RAY) * (Number(m.mat) / RAY)
        : 0
    const collPrice = spotDerivedPrice || (prices[collPriceKey] ?? 0)

    // Total debt: Art (wad) × rate (ray) = rad.
    const totalDebt =
      m.Art !== null && m.rate !== null
        ? Number((m.Art * m.rate) / BigInt(1e27)) / 10 ** debtDecimals
        : 0
    const totalColl =
      m.joinBalance !== null ? toHuman(m.joinBalance, collDecimals) : 0

    const mat =
      m.mat !== null ? Number(m.mat) / RAY : Number(market.mat) / RAY || 1.5
    const ltv = mat > 0 ? 1 / mat : 0
    // Dog.chop is a wad multiplier (1.13e18 = 13 % penalty).
    const chop = market.chop ? Number(market.chop) / WAD : 0
    const liqPenalty = chop > 1 ? chop - 1 : 0

    // Nominal-APR stability fee — same convention as the savings dsr leg.
    const duty = m.duty !== null ? m.duty : BigInt(market.duty ?? 0)
    const borrowApr =
      duty > BigInt(1e27)
        ? (Number(duty - BigInt(10) ** BigInt(27)) / RAY) * YEAR_SECONDS * 100
        : 0

    // Mintable headroom: the ilk debt ceiling (rad), the market-wide binding
    // constraint. Per-urn safety (spot) binds per position, not here.
    const line = m.line !== null ? Number(m.line) / RAD : 0
    let borrowLiquidity = Math.max(0, line - totalDebt)
    // A zeroed ceiling is Maker's "unfiled / retired / OFFBOARDED ilk" state —
    // Sky's offboarded ilks (the WBTC book) read exactly this way, and the
    // metadata flag is carried through as a second, explicit signal.
    const offboarded = (market as any).offboarded === true
    const halted = line === 0 || offboarded

    // @ts-ignore — built incrementally, matches the River/Liquity converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. COLLATERAL entry — deposit-only.
    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: market.gemJoin?.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      totalDeposits: totalColl,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: totalColl,
      borrowLiquidity: 0,
      totalLiquidityUSD: totalColl * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: totalColl * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          collateralFactor: ltv,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: !halted,
      hasStable: false,
      isActive: !halted,
      isFrozen: halted,
    } as any

    // 2. LOAN entry — the dss stable (Vat-minted; no protocol supply side).
    const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: debtSymbol,
      poolId: cfg.vat.toLowerCase(),
      underlying: debtAddr,
      asset: currencyFor(debtAddr, debtDecimals, debtSymbol, tokens),
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * debtPrice,
      borrowLiquidityUSD: borrowLiquidity * debtPrice,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * debtPrice,
      utilization: 0,
      // The earn side is the savings token (savings provider), not a lending
      // deposit.
      depositRate: 0,
      variableBorrowRate: borrowApr,
      stableBorrowRate: 0,
      // Governance-set per-ilk stability fee — no utilization curve.
      rateModel: 'protocolSet',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: debtDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: halted,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !halted,
      depositsEnabled: false,
      hasStable: false,
      variableBorrowDisabled: halted,
      isActive: !halted,
      isFrozen: halted,
    } as any

    // --- dss descriptor (metadata + live snapshot; consumed by the calldata
    // builders, the term-sheet adapter and worker-api resolvers) ---
    const descriptor = {
      ilk: market.ilk,
      /** Per-second stability fee (ray string) — live, metadata fallback. */
      duty: duty.toString(),
      /** Liquidation ratio (ray string). */
      mat: m.mat !== null ? m.mat.toString() : market.mat,
      /** Liquidation penalty multiplier (wad string, Dog.chop). */
      chop: market.chop,
      /** Ilk debt ceiling / per-CDP floor (rad strings). */
      line: m.line !== null ? m.line.toString() : market.line,
      dust: m.dust !== null ? m.dust.toString() : market.dust,
      /** Debt accumulator (ray string) — art × rate = debt. */
      rate: m.rate !== null ? m.rate.toString() : undefined,
      /** Governance has retired the ilk: no new debt, exits only. */
      offboarded,
      addresses: {
        vat: cfg.vat,
        jug: cfg.jug,
        spot: cfg.spot,
        dog: cfg.dog,
        cdpManager: cfg.cdpManager,
        proxyActions: cfg.proxyActions,
        proxyRegistry: cfg.proxyRegistry,
        /** DaiJoin-equivalent — the debt enter/exit adapter. */
        debtJoin,
        /** @deprecated brand-specific alias of `debtJoin`. */
        usddJoin: debtJoin,
        gemJoin: market.gemJoin,
        clip: market.clip,
        pip: market.pip,
      },
    }

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName,
        loanDecimals: debtDecimals,
        collateralDecimals: collDecimals,
        // The gem join doubles as the market id (marketUid address) — one
        // adapter per ilk, like River's TroveManager.
        id: market.gemJoin?.toLowerCase(),
        lltv: m.mat !== null ? m.mat.toString() : market.mat,
        oracle: market.pip ?? zeroAddress,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: debtAddr,
        dss: descriptor,
        // Back-compat duplicate: worker-api + the term-sheet input list read
        // `market.usdd`. Same object, so the two can never drift.
        usdd: descriptor,
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}

/** @deprecated brand-specific alias — use `convertDssMarketsToResponse`. */
export const convertUsddMarketsToResponse = convertDssMarketsToResponse
/** @deprecated brand-specific alias — use `dssLenderKey`. */
export const usddLenderKey = dssLenderKey
/** @deprecated brand-specific alias — use `dssKeyParts`. */
export const usddKeyParts = dssKeyParts
